/**
 * @flow
 */

import React from 'react';
import { Field } from 'redux-form';
import styled from 'styled-components';
import Reaptcha from 'reaptcha';
import {
    Modal,
    Form,
    Input,
    Message,
    Icon,
    Image,
    Header as BaseHeader,
} from 'semantic-ui-react';
import { FormattedMessage } from 'react-intl';
import { mapProps } from 'recompose';
import AuthLayout from '../../../layouts/components/AuthLayout';
import { FormField, FormLabel, Button, PhoneInput } from '../../../components';

import type { ReduxForm } from '../../../type';
import config from '../../../config';

type SignupPageProps = {} & ReduxForm;

const fields = {
    input: FormField(mapProps(({ meta, ...props }) => ({ ...props }))(Input)),
    phone: FormField(PhoneInput),
};

const Actions = styled(Modal.Actions)`
  margin-top: 2rem;
`;

const SubmitButton = styled(Button)`
  margin: 0px !important;
  padding-top: 1em !important;
  padding-bottom: 1em !important;
  text-transform: uppercase !important;
`;

const Logo = styled(Image)`
  margin: 0 auto 3em auto;
`;

const Header = styled(BaseHeader)`
  &.header {
    font-size: 36px !important;
    text-align: center;
  }
`;

const SignupForm = ({
    handleSubmit,
    pristine,
    submitting,
    token,
    setToken,
    error,
}: SignupPageProps) => (
    <AuthLayout>
        <Logo src="/images/appotime-logo-dashboard.png" />
        <Header as="h1">
            <FormattedMessage id="pages.signup.title" defaultMessage="Registration" />
        </Header>
        {error && (
            <Message error>
                <Icon name="warning sign" />
                {error}
            </Message>
        )}
        <Form onSubmit={handleSubmit}>
            <Input name="token" value={token} type="hidden" />
            <Field
                component={fields.input}
                name="salonName"
                label={
                    <FormLabel>
                        <FormattedMessage
                            id="pages.signup.form.salonName"
                            defaultMessage="Salon name"
                        />
                    </FormLabel>
                }
                type="text"
                size="big"
                simpleValue
            />
            <Field
                component={fields.input}
                name="salonAddress"
                label={
                    <FormLabel>
                        <FormattedMessage
                            id="pages.signup.form.salonAddress"
                            defaultMessage="Salon address"
                        />
                    </FormLabel>
                }
                type="text"
                size="big"
                simpleValue
            />
            <Field
                component={fields.input}
                name="firstName"
                label={
                    <FormLabel>
                        <FormattedMessage
                            id="pages.signup.form.firstName"
                            defaultMessage="Your first name"
                        />
                    </FormLabel>
                }
                type="text"
                size="big"
                simpleValue
            />
            <Field
                component={fields.input}
                name="lastName"
                label={
                    <FormLabel>
                        <FormattedMessage
                            id="pages.signup.form.lastName"
                            defaultMessage="Your last name"
                        />
                    </FormLabel>
                }
                type="text"
                size="big"
                simpleValue
            />
            <Field
                component={fields.input}
                name="email"
                label={
                    <FormLabel>
                        <FormattedMessage
                            id="pages.signup.form.email"
                            defaultMessage="Your e-mail address"
                        />
                    </FormLabel>
                }
                type="text"
                size="big"
                simpleValue
            />
            <Field
                component={fields.phone}
                name="phone"
                label={
                    <FormLabel>
                        <FormattedMessage
                            id="pages.signup.form.phone"
                            defaultMessage="Your phone number"
                        />
                    </FormLabel>
                }
                type="text"
                size="big"
                simpleValue
            />
            <Field
                component={fields.input}
                name="password"
                label={
                    <FormLabel>
                        <FormattedMessage
                            id="pages.signup.form.password"
                            defaultMessage="Password"
                        />
                    </FormLabel>
                }
                type="password"
                size="big"
                simpleValue
            />
            <div className="dimmer">
                <Reaptcha
                    sitekey={config.recaptcha.key}
                    onVerify={t => {
                        setToken(t);
                    }}
                />
            </div>
        </Form>
        <Actions>
            <SubmitButton
                onClick={handleSubmit}
                disabled={submitting || pristine || !token}
                loading={submitting}
                primary
                fluid
            >
                <FormattedMessage
                    id="pages.signup.button.create"
                    defaultMessage="Create account"
                />
            </SubmitButton>
        </Actions>
    </AuthLayout>
);

export default SignupForm;
